import {
  coreExtensionData,
  ErrorDisplay,
  NotFoundErrorPage,
  PageLayout,
  Progress,
} from '@backstage/frontend-plugin-api';
import type { FrontendFeature } from '@backstage/frontend-plugin-api';
import { IconBundleBlueprint } from '@backstage/plugin-app-react';
import appPlugin from '@backstage/plugin-app';
import { KernAppLayout } from './layout/KernAppLayout';
import { KernErrorDisplay } from './components/KernErrorDisplay';
import { KernNotFoundPage } from './components/KernNotFoundPage';
import { KernPageLayout } from './components/KernPageLayout';
import { KernProgress } from './components/KernProgress';
import { kernIcons } from './components/kernIcons';
import { KernSignInPage } from './signin/KernSignInPage';
import { oidcAuthApi } from './signin/oidcAuthApi';
import { kernDarkAppTheme, kernLightAppTheme } from './theme/kernAppThemes';

/**
 * Single place for all overrides of the app plugin.
 *
 * Every `withOverrides` call creates a new plugin instance — so layout, theme and
 * component replacements have to live together here instead of in separate modules.
 */
export const appOverrides: FrontendFeature = appPlugin.withOverrides({
  extensions: [
    // Page frame: KERN instead of SidebarPage
    appPlugin.getExtension('app/layout').override({
      factory(_originalFactory, { inputs }) {
        return [
          coreExtensionData.reactElement(
            <KernAppLayout
              nav={inputs.nav.get(coreExtensionData.reactElement)}
              content={inputs.content.get(coreExtensionData.reactElement)}
            />,
          ),
        ];
      },
    }),

    // The built-in themes are replaced, not extended: the theme picker in the settings
    // should offer KERN only.
    appPlugin.getExtension('theme:app/light').override({
      params: { theme: kernLightAppTheme },
    }),
    appPlugin.getExtension('theme:app/dark').override({
      params: { theme: kernDarkAppTheme },
    }),

    // Core components that Backstage uses across the whole UI.
    appPlugin.getExtension('component:app/core-progress').override({
      params: defineParams =>
        defineParams({ component: Progress, loader: () => KernProgress }),
    }),
    appPlugin.getExtension('component:app/core-error-display').override({
      params: defineParams =>
        defineParams({
          component: ErrorDisplay,
          loader: () => KernErrorDisplay,
        }),
    }),
    appPlugin.getExtension('component:app/core-not-found-error-page').override({
      params: defineParams =>
        defineParams({
          component: NotFoundErrorPage,
          loader: () => KernNotFoundPage,
        }),
    }),
    appPlugin.getExtension('component:app/core-page-layout').override({
      params: defineParams =>
        defineParams({ component: PageLayout, loader: () => KernPageLayout }),
    }),

    // Access to the OIDC provider used by the sign-in page.
    oidcAuthApi,

    // Sign-in page in the KERN frame (guest locally, Keycloak in production).
    appPlugin.getExtension('sign-in-page:app').override({
      params: { loader: async () => KernSignInPage },
    }),

    // KERN icons wherever KERN provides a matching symbol.
    IconBundleBlueprint.make({
      name: 'kern',
      params: { icons: kernIcons },
    }),
  ],
});
